import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Monitor, Smartphone, Save, FolderOpen, Trash2, Upload, ChevronUp, ExternalLink, LayoutGrid } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ORIENTATIONS = [
  { value: '1920x1080', label: '1920 x 1080', desc: 'Landscape', icon: Monitor, w: 1920, h: 1080 },
  { value: '1080x1920', label: '1080 x 1920', desc: 'Portrait', icon: Smartphone, w: 1080, h: 1920 },
];

const TAG_COLORS = {
  'K.O.': '#dc2626', 'M.E.': '#16a34a', 'Multi day': '#3b82f6',
  'Turbo': '#eab308', 'Hyper Turbo': '#f97316', 'Super Hyper Turbo': '#f97316',
  'Satellite': '#8b5cf6', 'Stack Choice': '#ec4899',
};

const DRAGON_IMAGE = "https://static.prod-images.emergentagent.com/jobs/2e421070-bee1-421e-b8d4-a5b3287448e7/images/d50153520bf6491265930045a154560b5b35949e0f1beea5ecc0cf5f92e4139c.png";

export default function BillboardCreator({ user }) {
  const navigate = useNavigate();
  const token = user?.token;
  const headers = { Authorization: `Bearer ${token}` };

  const [orientation, setOrientation] = useState('1920x1080');
  const [dates, setDates] = useState([]);
  const [allDates, setAllDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [autoDaily, setAutoDaily] = useState(false);
  const [seriesList, setSeriesList] = useState([]);
  const [selectedSeriesId, setSelectedSeriesId] = useState('all');
  const [tournaments, setTournaments] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [settings, setSettings] = useState({});

  const [heroImage, setHeroImage] = useState(DRAGON_IMAGE);
  const [bgImage, setBgImage] = useState('');
  const [uploadingBg, setUploadingBg] = useState(false);

  // Templates
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);

  // CTA
  const [ctaText, setCtaText] = useState('');
  const [ctaEnabled, setCtaEnabled] = useState(false);

  const fetchDates = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/tournaments/dates`);
      // Show every date the API returns. Filtering by Series happens client-side
      // via the dedicated dropdown below.
      setAllDates(data);
      setDates(data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchSeries = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/series`);
      setSeriesList(data);
      // Auto-pick the active series (live > upcoming > most recent) so the date
      // list opens already scoped to the relevant festival.
      if (data.length > 0) {
        const active = data.find(s => s.status === 'opened')
          || data.find(s => s.status === 'upcoming')
          || data[0];
        if (active) setSelectedSeriesId(active.id);
      }
    } catch (e) { console.error(e); }
  }, []);

  // Re-derive the visible date list whenever the chosen series changes.
  // Also auto-select a sensible default date so the preview shows data
  // immediately — today's date when it falls inside the series window,
  // otherwise the first available date in the filtered list.
  useEffect(() => {
    const todayKST = (() => {
      const d = new Date();
      // KST = UTC+9
      const utc = d.getTime() + d.getTimezoneOffset() * 60000;
      const kst = new Date(utc + 9 * 3600000);
      const y = kst.getFullYear();
      const m = String(kst.getMonth() + 1).padStart(2, '0');
      const day = String(kst.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    })();

    let filtered = allDates;
    if (selectedSeriesId !== 'all' && seriesList.length) {
      const s = seriesList.find(x => x.id === selectedSeriesId);
      if (s) filtered = allDates.filter(d => d.date >= s.start_date && d.date <= s.end_date);
    }
    setDates(filtered);

    if (filtered.length === 0) {
      setSelectedDate('');
      return;
    }
    // Decide the default selection priority:
    //   1) keep current pick if still valid
    //   2) today (KST) if it's in the list
    //   3) the first date in the filtered list
    const stillValid = selectedDate && filtered.some(d => d.date === selectedDate);
    if (stillValid) return;
    const todayInList = filtered.some(d => d.date === todayKST);
    setSelectedDate(todayInList ? todayKST : filtered[0].date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSeriesId, allDates, seriesList]);

  const fetchTournaments = useCallback(async (date) => {
    if (!date) return;
    try {
      const { data } = await axios.get(`${API}/tournaments`, { params: { date } });
      setTournaments(data);
      setSelectedIds(new Set(data.map(t => t.id)));
    } catch (e) { console.error(e); }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/settings`);
      setSettings(data);
      if (data.hero_image_url) setHeroImage(data.hero_image_url);
    } catch (e) { console.error(e); }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/templates`, { headers: { Authorization: `Bearer ${token}` } });
      setTemplates(data);
    } catch (e) { console.error(e); }
  }, [token]);

  useEffect(() => { fetchDates(); fetchSettings(); fetchTemplates(); fetchSeries(); }, [fetchDates, fetchSettings, fetchTemplates, fetchSeries]);
  useEffect(() => { if (selectedDate) fetchTournaments(selectedDate); }, [selectedDate, fetchTournaments]);

  const toggleTournament = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(tournaments.map(t => t.id)));
  const selectNone = () => setSelectedIds(new Set());

  // Template functions
  const saveTemplate = async () => {
    if (!templateName.trim()) return;
    // Build stable composite keys from the current selection so the template
    // survives live PokerLens syncs (which rotate Mongo _id values).
    const eventKeys = tournaments
      .filter(t => selectedIds.has(t.id))
      .map(t => `${t.event_number}|${t.time}`);
    try {
      await axios.post(`${API}/admin/templates`, {
        name: templateName.trim(),
        orientation,
        date: selectedDate,
        selected_event_keys: eventKeys,
        hero_image_url: heroImage,
        bg_image_url: bgImage,
        cta_enabled: ctaEnabled,
        cta_text: ctaText,
      }, { headers });
      setTemplateName('');
      setShowSaveDialog(false);
      fetchTemplates();
    } catch (e) { alert('Failed to save template'); }
  };

  const loadTemplate = async (tpl) => {
    setOrientation(tpl.orientation || '1920x1080');
    if (tpl.date) {
      setSelectedDate(tpl.date);
      try {
        const { data } = await axios.get(`${API}/tournaments`, { params: { date: tpl.date } });
        setTournaments(data);
        // Prefer stable composite keys. Fall back to legacy Mongo-ID list for
        // backward compatibility with templates saved before the migration.
        if (tpl.selected_event_keys?.length > 0) {
          const keys = new Set(tpl.selected_event_keys);
          setSelectedIds(new Set(
            data.filter(t => keys.has(`${t.event_number}|${t.time}`)).map(t => t.id)
          ));
        } else if (tpl.selected_tournament_ids?.length > 0) {
          setSelectedIds(new Set(tpl.selected_tournament_ids));
        } else {
          setSelectedIds(new Set(data.map(t => t.id)));
        }
      } catch (e) { console.error(e); }
    }
    if (tpl.hero_image_url) setHeroImage(tpl.hero_image_url);
    if (tpl.bg_image_url) setBgImage(tpl.bg_image_url);
    setCtaEnabled(tpl.cta_enabled || false);
    setCtaText(tpl.cta_text || '');
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await axios.delete(`${API}/admin/templates/${id}`, { headers });
      fetchTemplates();
    } catch (e) { alert('Failed to delete'); }
  };

  // Hero image upload
  const handleHeroUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploadingHero(true);
      const fd = new FormData();
      fd.append('file', file);
      try {
        const { data } = await axios.post(`${API}/admin/upload`, fd, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        });
        const fileUrl = `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.path}`;
        setHeroImage(fileUrl);
        await axios.put(`${API}/admin/settings`, { hero_image_url: fileUrl }, { headers });
      } catch (err) { alert('Upload failed'); }
      finally { setUploadingHero(false); }
    };
    input.click();
  };

  // Background image upload
  const handleBgUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploadingBg(true);
      const fd = new FormData();
      fd.append('file', file);
      try {
        const { data } = await axios.post(`${API}/admin/upload`, fd, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        });
        const fileUrl = `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.path}`;
        setBgImage(fileUrl);
      } catch (err) { alert('Upload failed'); }
      finally { setUploadingBg(false); }
    };
    input.click();
  };

  const selectedTournaments = tournaments.filter(t => selectedIds.has(t.id));
  const orient = ORIENTATIONS.find(o => o.value === orientation) || ORIENTATIONS[0];
  const isLandscape = orient.w > orient.h;

  const formatDateHeading = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const [syncing, setSyncing] = useState(false);

  // Today's date in Korea time (UTC+9) — used for the "Live" auto-advance mode.
  const computeTodayKst = () => {
    const now = new Date();
    const kst = new Date(now.getTime() + (9 * 60 - now.getTimezoneOffset()) * 60 * 1000);
    return `${kst.getFullYear()}-${String(kst.getMonth() + 1).padStart(2, '0')}-${String(kst.getDate()).padStart(2, '0')}`;
  };

  const handleCreate = () => {
    const params = new URLSearchParams();
    // Live mode: omit date (and event selection) so the board always shows
    // today's schedule and rolls over automatically at 00:00 KST.
    if (!autoDaily) {
      params.set('date', selectedDate);
      if (selectedIds.size < tournaments.length && selectedIds.size > 0) {
        const keys = tournaments
          .filter(t => selectedIds.has(t.id))
          .map(t => `${t.event_number}|${t.time}`)
          .join(',');
        params.set('evs', keys);
      }
    }
    params.set('orientation', orientation);
    if (ctaEnabled && ctaText) params.set('cta', ctaText);
    if (bgImage) params.set('bg', bgImage);
    window.open(`/billboard/view?${params.toString()}`, '_blank');
  };

  const handleCreateFloor = () => {
    const params = new URLSearchParams();
    if (!autoDaily && selectedDate) params.set('date', selectedDate);
    if (bgImage) params.set('bg', bgImage);
    window.open(`/billboard/floor?${params.toString()}`, '_blank');
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/admin/sync-pokerlens`, {}, { headers });
      if (selectedDate) {
        const { data } = await axios.get(`${API}/tournaments`, { params: { date: selectedDate } });
        setTournaments(data);
        setSelectedIds(new Set(data.map(t => t.id)));
      }
      fetchDates();
    } catch (e) { console.error('Sync failed:', e); }
    finally { setSyncing(false); }
  };

  // Extra columns count
  const extraCols = 0;

  return (
    <div className="min-h-screen bg-background-base" data-testid="billboard-creator">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background-surface/95 backdrop-blur border-b border-white/[0.06]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="text-white/50 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <span className="font-heading text-lg font-bold text-white">Billboard Creator</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing}
              className="border-white/10 text-white/60 hover:text-white hover:bg-white/5 font-heading tracking-wider"
              data-testid="sync-pokerlens-btn">
              {syncing ? 'Syncing...' : 'Sync'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowSaveDialog(true)}
              disabled={!selectedDate}
              className="border-white/10 text-white/60 hover:text-white hover:bg-white/5 font-heading tracking-wider"
              data-testid="save-template-button">
              <Save className="w-4 h-4 mr-1" /> Save Template
            </Button>
            <Button onClick={handleCreate} disabled={autoDaily ? false : (!selectedDate || selectedTournaments.length === 0)}
              className="bg-primary-main text-black hover:bg-primary-hover font-heading tracking-wider"
              data-testid="create-billboard-button">
              <ExternalLink className="w-4 h-4 mr-2" /> Create {autoDaily ? 'Live' : ''} {orientation}
            </Button>
            <Button onClick={handleCreateFloor} disabled={autoDaily ? false : !selectedDate}
              variant="outline"
              className="border-secondary-main/50 text-secondary-main hover:bg-secondary-main/10 font-heading tracking-wider"
              data-testid="create-floor-plan-button">
              <LayoutGrid className="w-4 h-4 mr-2" /> Floor Plan {autoDaily ? 'Live' : ''} 1080x1920
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 xl:grid-cols-[340px_1fr] gap-6">
          {/* ===== LEFT: Controls ===== */}
          <div className="space-y-5 xl:max-h-[calc(100vh-120px)] xl:overflow-y-auto xl:pr-2" style={{ scrollbarWidth: 'thin', scrollbarColor: '#b91c1c #0a0505' }}>
            {/* Step 1: Orientation */}
            <div className="bg-background-surface border border-white/[0.06] rounded-xl p-5">
              <h4 className="font-heading text-sm tracking-wider uppercase text-primary-main mb-3">1. Orientation</h4>
              <div className="grid grid-cols-2 gap-2">
                {ORIENTATIONS.map(o => (
                  <button key={o.value} onClick={() => setOrientation(o.value)}
                    data-testid={`orientation-${o.value}`}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border transition-all ${
                      orientation === o.value ? 'border-primary-main bg-primary-main/10 text-white' : 'border-white/[0.06] text-white/40 hover:text-white/70'
                    }`}>
                    <o.icon className="w-5 h-5" />
                    <span className="font-mono text-xs">{o.label}</span>
                    <span className="font-body text-[10px] opacity-60">{o.desc}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Step 2: Series filter */}
            <div className="bg-background-surface border border-white/[0.06] rounded-xl p-5">
              <h4 className="font-heading text-sm tracking-wider uppercase text-primary-main mb-3">2. Series (optional)</h4>
              <Select value={selectedSeriesId} onValueChange={setSelectedSeriesId}>
                <SelectTrigger className="bg-background-base border-white/10 text-white" data-testid="billboard-series-select">
                  <SelectValue placeholder="All dates" />
                </SelectTrigger>
                <SelectContent className="bg-background-surface border-white/10 text-white">
                  <SelectItem value="all">All dates ({allDates.length})</SelectItem>
                  {seriesList.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name} · {s.start_date} → {s.end_date}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-2 font-mono text-[10px] text-white/30">Filter the date list to a specific festival.</p>
            </div>

            {/* Step 3: Date */}
            <div className="bg-background-surface border border-white/[0.06] rounded-xl p-5">
              <h4 className="font-heading text-sm tracking-wider uppercase text-primary-main mb-3">3. Select Date</h4>

              {/* Live auto-advance toggle */}
              <label className="flex items-start gap-2.5 mb-3 cursor-pointer rounded-lg border border-white/[0.06] p-3 hover:border-primary-main/30 transition-colors" data-testid="billboard-auto-daily-toggle">
                <input
                  type="checkbox"
                  checked={autoDaily}
                  onChange={(e) => {
                    const c = e.target.checked;
                    setAutoDaily(c);
                    if (c) setSelectedDate(computeTodayKst());
                  }}
                  className="mt-0.5 w-4 h-4 accent-primary-main"
                  data-testid="billboard-auto-daily-checkbox"
                />
                <span className="font-body text-xs text-white/80 leading-snug">
                  <span className="text-white font-semibold">Live — auto-advance daily</span><br />
                  Always shows today's schedule and rolls over to the next day at midnight (KST). No manual date needed.
                </span>
              </label>

              <Select value={selectedDate} onValueChange={setSelectedDate} disabled={autoDaily}>
                <SelectTrigger className="bg-background-base border-white/10 text-white disabled:opacity-40" data-testid="billboard-date-select">
                  <SelectValue placeholder={dates.length ? 'Choose a date...' : 'No dates available'} />
                </SelectTrigger>
                <SelectContent className="bg-background-surface border-white/10 text-white">
                  {dates.map(d => (
                    <SelectItem key={d.date} value={d.date}>
                      {d.date} ({d.day_name}) — {d.tournament_count} events
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {autoDaily ? (
                <p className="mt-2 font-mono text-[10px] text-emerald-400/80">
                  Live mode on — the board follows today's date automatically.
                </p>
              ) : dates.length === 0 && (
                <p className="mt-2 font-mono text-[10px] text-amber-400/80">
                  No dates match the selected series. Try "All dates" or click Sync.
                </p>
              )}
            </div>

            {/* Step 3: Tournaments */}
            {tournaments.length > 0 && (
              <div className="bg-background-surface border border-white/[0.06] rounded-xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-heading text-sm tracking-wider uppercase text-primary-main">4. Tournaments</h4>
                  <div className="flex gap-2">
                    <button onClick={selectAll} className="text-[10px] text-white/40 hover:text-white">All</button>
                    <button onClick={selectNone} className="text-[10px] text-white/40 hover:text-white">None</button>
                  </div>
                </div>
                <div className="space-y-1.5 max-h-[300px] overflow-y-auto pr-1">
                  {tournaments.map(t => (
                    <label key={t.id} className="flex items-center gap-2.5 py-1.5 px-2 rounded hover:bg-white/[0.03] cursor-pointer">
                      <Checkbox checked={selectedIds.has(t.id)} onCheckedChange={() => toggleTournament(t.id)} data-testid={`select-tournament-${t.id}`} />
                      <div className="flex-1 min-w-0">
                        <span className="font-heading text-xs text-white truncate block">{t.event_name}</span>
                        <span className="font-mono text-[10px] text-white/30">#{t.event_number} · {t.time}</span>
                      </div>
                    </label>
                  ))}
                </div>
                <p className="mt-2 font-mono text-[10px] text-white/30">{selectedIds.size}/{tournaments.length} selected</p>
              </div>
            )}

            {/* Step 4: Images */}
            <div className="bg-background-surface border border-white/[0.06] rounded-xl p-5">
              <h4 className="font-heading text-sm tracking-wider uppercase text-primary-main mb-4">5. Images</h4>
              
              {/* Background */}
              <div>
                <Label className="text-white/60 text-[10px] uppercase tracking-wider mb-2 block">Background</Label>
                <Button variant="outline" size="sm" onClick={handleBgUpload} disabled={uploadingBg}
                  className="border-white/10 text-white/60 hover:text-white hover:bg-white/5 w-full mb-2"
                  data-testid="upload-bg-image">
                  <Upload className="w-3.5 h-3.5 mr-1.5" /> {uploadingBg ? 'Uploading...' : 'Upload Background'}
                </Button>
                {bgImage && (
                  <div className="rounded-lg overflow-hidden border border-white/[0.06] h-14 bg-black mb-1">
                    <img src={bgImage} alt="BG" className="w-full h-full object-cover opacity-80" />
                  </div>
                )}
                <button onClick={() => setBgImage('')} className="text-[10px] text-white/20 hover:text-white/40">Remove background</button>
              </div>
            </div>

            {/* Step 5: Call to Action */}
            <div className="bg-background-surface border border-white/[0.06] rounded-xl p-5">
              <h4 className="font-heading text-sm tracking-wider uppercase text-primary-main mb-3">
                <ChevronUp className="w-4 h-4 inline mr-1.5 -mt-0.5" />6. Call to Action
              </h4>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-white/60 text-xs">Enable CTA Banner</Label>
                <Switch checked={ctaEnabled} onCheckedChange={setCtaEnabled} data-testid="toggle-cta" />
              </div>
              {ctaEnabled && (
                <div className="space-y-3">
                  <Input
                    value={ctaText}
                    onChange={(e) => setCtaText(e.target.value)}
                    placeholder="Swipe Up for Full Schedule"
                    className="bg-background-base border-white/10 text-white text-sm"
                    data-testid="cta-text-input"
                  />
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      'Swipe Up for Full Schedule',
                      'Register Now at playreddragon.com',
                      'Link in Bio for Details',
                      'Scan QR Code to Register',
                      'Follow @playreddragon for Updates',
                    ].map(preset => (
                      <button key={preset} onClick={() => setCtaText(preset)}
                        className="px-2 py-1 rounded bg-white/[0.04] hover:bg-white/[0.08] text-[9px] text-white/40 hover:text-white/70 transition-colors"
                        data-testid={`cta-preset-${preset.slice(0, 10)}`}>
                        {preset}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Saved Templates */}
            <div className="bg-background-surface border border-white/[0.06] rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-heading text-sm tracking-wider uppercase text-primary-main">
                  <FolderOpen className="w-4 h-4 inline mr-1.5 -mt-0.5" />Saved Templates
                </h4>
                <span className="font-mono text-[10px] text-white/20">{templates.length}</span>
              </div>
              {templates.length === 0 ? (
                <p className="text-xs text-white/20 text-center py-4">No saved templates yet</p>
              ) : (
                <div className="space-y-1.5 max-h-[200px] overflow-y-auto pr-1">
                  {templates.map(tpl => (
                    <div key={tpl.id} className="flex items-center gap-2 py-2 px-2.5 rounded-lg hover:bg-white/[0.03] group">
                      <button onClick={() => loadTemplate(tpl)} className="flex-1 text-left min-w-0" data-testid={`load-template-${tpl.id}`}>
                        <span className="font-heading text-xs text-white block truncate">{tpl.name}</span>
                        <span className="font-mono text-[9px] text-white/25">{tpl.orientation} · {tpl.date || 'no date'}</span>
                      </button>
                      <button onClick={() => deleteTemplate(tpl.id)} className="text-white/20 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                        data-testid={`delete-template-${tpl.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Save Template Dialog */}
          {showSaveDialog && (
            <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-4">
              <div className="bg-background-surface border border-white/[0.1] rounded-xl p-6 w-full max-w-sm" data-testid="save-template-dialog">
                <h4 className="font-heading text-base text-white mb-4">Save Billboard Template</h4>
                <Input
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="e.g. April 13 Landscape"
                  className="bg-background-base border-white/10 text-white mb-4"
                  data-testid="template-name-input"
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && saveTemplate()}
                />
                <div className="flex items-center gap-2">
                  <Button onClick={saveTemplate} disabled={!templateName.trim()}
                    className="bg-primary-main text-black hover:bg-primary-hover font-heading tracking-wider"
                    data-testid="confirm-save-template">
                    <Save className="w-4 h-4 mr-1" /> Save
                  </Button>
                  <Button variant="ghost" onClick={() => { setShowSaveDialog(false); setTemplateName(''); }} className="text-white/50 hover:text-white">
                    Cancel
                  </Button>
                </div>
                <p className="text-[10px] text-white/20 mt-3">Saves: orientation, date, selected tournaments, column toggles, hero image</p>
              </div>
            </div>
          )}
          <div className="flex flex-col items-center">
            <h4 className="font-heading text-sm tracking-wider uppercase text-white/40 mb-3">Preview</h4>
            <div className="w-full overflow-auto border border-white/[0.06] rounded-xl bg-black/30 p-4 flex justify-center">
              {/* Scaled preview wrapper */}
              <div style={{
                width: orient.w,
                height: orient.h,
                transform: `scale(${Math.min(1, (window.innerWidth - 420) / orient.w, 0.65)})`,
                transformOrigin: 'top center',
              }}>
                {/* THE BILLBOARD */}
                <div
                  data-testid="billboard-preview"
                  style={{
                    width: orient.w,
                    height: orient.h,
                    background: '#080404',
                    backgroundImage: bgImage ? `url(${bgImage})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    fontFamily: "Arial, sans-serif",
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {/* Ambient glow */}
                  <div style={{ position: 'absolute', top: 0, left: '20%', width: 400, height: 300, borderRadius: '50%', background: 'radial-gradient(circle, rgba(185,28,28,0.1) 0%, transparent 70%)' }} />
                  {/* Full-brightness background — no global dark overlay so the
                      uploaded banner keeps its original quality. */}

                  {/* === HERO SPACER — plain; branding comes from the uploaded background === */}
                  <div style={{ height: isLandscape ? 300 : 400, flexShrink: 0, zIndex: 1 }} />

                  {/* Red/gold accent line */}
                  <div style={{ height: 2, background: 'linear-gradient(to right, transparent, #b91c1c, #d4af37, #b91c1c, transparent)', flexShrink: 0 }} />

                  {/* === DATE HEADING === */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: isLandscape ? '14px 40px 8px' : '12px 24px 6px', flexShrink: 0, position: 'relative', zIndex: 1 }}>
                    <div style={{ fontSize: 40, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#fff', fontFamily: "Arial, sans-serif", textAlign: 'center' }}>
                      Daily Tournament Schedule
                    </div>
                    <div style={{ fontSize: 30, color: '#facc15', fontFamily: "Arial, sans-serif", textAlign: 'center', marginTop: 4 }}>
                      {formatDateHeading(selectedDate)}
                    </div>
                  </div>

                  {/* === TABLE === */}
                  <div style={{ flex: 1, overflow: 'hidden', padding: isLandscape ? '0 40px 16px' : '0 24px 12px', position: 'relative', zIndex: 1 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                      <thead>
                        <tr style={{ background: 'rgba(14,7,7,0.8)' }}>
                          <th style={{ ...thStyle, width: '7%' }}>TIME</th>
                          <th style={{ ...thStyle, width: '4%' }}>#</th>
                          <th style={{ ...thStyle, width: '30%', textAlign: 'left' }}>EVENT</th>
                          <th style={{ ...thStyle, width: '16%', textAlign: 'left' }}>BUY-IN</th>
                          <th style={{ ...thStyle, width: '8%' }}>STACK</th>
                          <th style={{ ...thStyle, width: '8%' }}>DURATION</th>
                          <th style={{ ...thStyle, width: '8%' }}>REG CLOSE</th>
                          <th style={{ ...thStyle, width: '10%' }}>ENTRIES</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedTournaments.map((t, idx) => {
                          const rowH = isLandscape
                            ? Math.min(68, Math.max(42, (orient.h - 500) / selectedTournaments.length - 2))
                            : Math.min(90, Math.max(48, (orient.h - 540) / selectedTournaments.length - 2));
                          const fontSize = rowH < 50 ? 11 : 13;
                          const isPremium = t.is_premium;
                          const bg = isPremium ? 'rgba(25,20,8,0.75)' : idx % 2 === 0 ? 'rgba(10,5,5,0.7)' : 'rgba(14,7,7,0.7)';
                          return (
                            <tr key={t.id} style={{ background: bg, borderBottom: '1px solid rgba(255,255,255,0.04)', borderLeft: isPremium ? '3px solid #d4af37' : '3px solid transparent' }}>
                              <td style={{ ...tdStyle, height: rowH, fontWeight: 700, color: '#facc15' }}>{t.time}</td>
                              <td style={{ ...tdStyle, height: rowH, color: '#ffffff', fontWeight: 600 }}>{t.event_number}</td>
                              <td style={{ ...tdStyle, height: rowH, textAlign: 'left', paddingLeft: 12 }}>
                                <div style={{ fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1.2 }}>
                                  {t.event_name}
                                </div>
                              </td>
                              <td style={{ ...tdStyle, height: rowH, textAlign: 'left', paddingLeft: 12, color: '#facc15' }}>
                                {t.buyin ? (
                                  <div>
                                    <div style={{ fontWeight: 700 }}>{t.buyin}</div>
                                    {t.buyin_breakdown && <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', fontWeight: 400 }}>({t.buyin_breakdown})</div>}
                                    {t.bounty && <div style={{ fontSize: 13, color: '#facc15', fontWeight: 600 }}>({t.bounty})</div>}
                                  </div>
                                ) : '-'}
                              </td>
                              <td style={{ ...tdStyle, height: rowH, color: '#ffffff' }}>{t.stack || '-'}</td>
                              <td style={{ ...tdStyle, height: rowH, color: '#ffffff' }}>{t.duration || '-'}</td>
                              <td style={{
                                ...tdStyle, height: rowH,
                                color: '#ffffff',
                                fontWeight: ['CLOSED','FINISHED','RUNNING'].includes(t.reg_close) ? 700 : 400,
                              }}>
                                {t.reg_close || '-'}
                                {t.reg_close === 'RUNNING' && (() => {
                                  if (!t.reg_close_time) return null;
                                  const [dp, tp] = t.reg_close_time.split(' ');
                                  if (!tp) return null;
                                  let sub = tp;
                                  if (t.date && dp && dp !== t.date) {
                                    const diff = Math.round((new Date(dp+'T00:00:00') - new Date(t.date+'T00:00:00')) / 86400000);
                                    if (diff > 0) sub = `${tp} +${diff}d`;
                                  }
                                  return <div style={{ fontSize: 11, fontWeight: 500, color: '#facc15', marginTop: 2 }}>closes {sub}</div>;
                                })()}
                              </td>
                              <td style={{ ...tdStyle, height: rowH, fontWeight: 800, fontSize: 22, color: '#facc15' }}>
                                {(() => {
                                  const players = t.total_players || 0;
                                  const entries = t.total_entries || t.total_entries_day || 0;
                                  const finished = t.reg_close === 'FINISHED';
                                  const hasWinner = t.winner_name || t.winner_prize;
                                  if (finished && hasWinner) {
                                    return (
                                      <div style={{ lineHeight: 1.15 }}>
                                        <div style={{ color: '#facc15', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.02em', fontSize: 13 }}>
                                          {'\u{1F3C6}'} {t.winner_name}
                                        </div>
                                        {t.winner_prize && <div style={{ color: '#ffffff', fontSize: 12, fontWeight: 600, marginTop: 1 }}>{t.winner_prize}</div>}
                                      </div>
                                    );
                                  }
                                  if (finished) {
                                    return <div style={{ color: '#ffffff', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Final — {entries} entries</div>;
                                  }
                                  if (players === 0 && entries === 0) return <span style={{ color: 'rgba(255,255,255,0.15)' }}>-</span>;
                                  return <>{players}<span style={{ margin: '0 4px', fontWeight: 600 }}>/</span>{entries}</>;
                                })()}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Footer accent */}
                  <div style={{ height: 2, background: 'linear-gradient(to right, transparent, #b91c1c, #d4af37, #b91c1c, transparent)', flexShrink: 0 }} />

                  {/* CTA Banner */}
                  {ctaEnabled && ctaText && (
                    <div style={{
                      flexShrink: 0,
                      padding: isLandscape ? '10px 40px' : '14px 24px',
                      background: 'linear-gradient(135deg, #b91c1c 0%, #8b0000 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 10,
                    }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 19V5M5 12l7-7 7 7"/>
                      </svg>
                      <span style={{
                        fontSize: isLandscape ? 14 : 16,
                        fontWeight: 700,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        color: '#fff',
                        fontFamily: "'Oswald', sans-serif",
                      }}>{ctaText}</span>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 19V5M5 12l7-7 7 7"/>
                      </svg>
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: isLandscape ? '8px 40px' : '6px 24px', flexShrink: 0 }}>
                    <span style={{ fontSize: 10, color: 'rgba(212,175,55,0.3)', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.2em' }}>#rdptplus</span>
                    <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.15)', fontFamily: "'Manrope', sans-serif" }}>playreddragon.com</span>
                    <span style={{ fontSize: 10, color: 'rgba(212,175,55,0.3)', fontFamily: "'JetBrains Mono', monospace" }}>RDPT PLUS+ 2026</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const thStyle = {
  padding: '8px 6px',
  fontSize: 15,
  fontWeight: 700,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: '#facc15',
  textAlign: 'center',
  fontFamily: "Arial, sans-serif",
};

const tdStyle = {
  padding: '4px 8px',
  textAlign: 'center',
  verticalAlign: 'middle',
  fontFamily: "Arial, sans-serif",
  fontSize: 17,
  fontWeight: 500,
};
